// src/store/streamStore.ts
/**
 * Tu Metadata
 * @tuScore 0.72 (Orchestrator - high justified complexity)
 * @tuGrade B
 * @tuRb 1.5 (central state hub: messages, realtime, sessions, typing, agent triggers)
 * @tuTime 4h (One Mic architecture + guard simplification Nov 30 - Dec 2, 2025)
 * @tuPattern one-mic-architecture, guard-clause, single-source-of-truth
 * @tuBaseline 0.58 (complex caching, race conditions, _lastInitializedConversation tracking)
 * @tuROI +0.04/hr (architecture wins, not pure refactor)
 * @lines 1731 (2,323 before Oct 2026 realtime carve → stream/realtime.ts, realtimeReducer.ts, agentObjects.ts, streamCache.ts)
 * @complexity 25 (justified - central message orchestrator)
 * @lastUpdated 2026-10-02
 *
 * Help Context
 * @tuPurpose "Central store for all chat/stream messages, sessions, and realtime sync"
 * @tuHelp "Manages message loading, sending, reactions, and realtime updates"
 * @tuUserAction "send-message, load-history"
 * @tuRelated ["StreamPage", "FloatingActiveChatV2", "feedStore", "userStore"]
 * @tuPrereq "User must be authenticated with active company"
 * @tuOutcome "Messages synced in realtime across all views"
 *
 * Architecture: ONE MIC (Mutual Exclusivity)
 * Key insight: With only ONE view loading messages at a time,
 * the complex caching layer (_messageCache, _lastInitializedConversation)
 * became unnecessary. Guard clause simplified to:
 *   if (activeStreamType === streamType && activeRecipientStreamId === recipientStreamId && messages.length > 0) return;
 *
 * Before: 50+ lines of cache logic, 3+ race condition bugs
 * After: 5 lines, zero race conditions
 *
 * Module map (Oct 2026): this file owns state + actions. Realtime lives in stream/:
 *   stream/realtime.ts        attachStreamRealtime() — channel, handlers, health, backfill
 *   stream/realtimeReducer.ts applyCommentEvent() — pure INSERT/UPDATE/DELETE reducer (tested)
 *   stream/agentObjects.ts    strip + hydrate [AGENT_CREATED_OBJECTS] envelopes (tested)
 *   stream/streamCache.ts     per-stream message cache helpers
 *   stream/agentInvoke.ts, agentTrigger.ts, sendPlan.ts, shareAttach.ts — send-side logic
 */
/**
 * .tuMetrics {"tu1": 0.78, "tu2": 0.82, "tu3": 7, "notes": "Stream message store: state + actions; realtime channel wiring and reducer carved into stream/realtime.ts + stream/realtimeReducer.ts (Oct 2026)", "tuVersion": "2.5"}
 * .help "Central state for stream messages, realtime updates, and sending logic."
 */
// 
// STREAM MESSAGE NOTIFICATION LOGIC
// =================================
// 
// IMPORTANT: Feed Events vs Indicator Dots
// -----------------------------------------
// 
// Feed Events (Feed page notifications):
// - ONLY created for: @mentions, Meet started, Announcements
// - Regular messages do NOT create feed events
// 
// Indicator Dots (Sidebar red pulsating dots):
// - Show for ALL unread messages (direct messages, team streams, standup streams)
// - Calculated from comments/messages table, NOT from feed events
// - Tracked via unreadDirectMessages, unreadTeamsCount, unreadStandupsCount in feedStore
// 
// This separation ensures:
// 1. Feed page doesn't get spammed with every message
// 2. Indicator dots still work for all unread messages
// 3. Notifications only appear for important events (@mentions, meets, announcements)
// 
// Core Principle: Recipients get notifications, senders don't
//
// FULL DOCUMENTATION: See docs/features/NOTIFICATION_SYSTEM_ARCHITECTURE.md
// This document explains the complete architecture, flow examples, and code locations.
//
// Clean, compatible, and commented: works for People↔People + People↔Agent,
// with symmetric DM backfill and safe realtime handling.
//
// Exposes the same API your Sidebar expects:
// - chatSessions, fetchRecentStandupSessions
// - selectedTeamStreamId, setSelectedTeamStreamId
// - selectedStandupId, setSelectedStandupId
// - selectedDMUserId, setSelectedDMUserId
//
// Agent-specific logic is clearly marked so edits don't break human DM flows.

import { create } from 'zustand';
import type { RealtimeChannel } from '@supabase/supabase-js';

import { Comment, ChatSession, InlineShareDraft } from '../types';
import { supabase } from '../lib/supabase';

import useUserStore from './userStore';
import useFeedStore from './feedStore';
import useTeamStore from './teamStore';
import useCompanyStore from './companyStore';
import { extractProposedActionsFromText, ProposedAction } from '../utils/agent';
import {
  fetchLatestStreamMessages,        // NEW: newest → oldest
  fetchMessagesBefore,              // NEW: page older via created_at
  insertStreamMessage,
  updateStreamMessageById,
  deleteStreamMessageById,
} from '../lib/supabase/queries/stream-queries';

import {
  fetchChatSessions,
  fetchChatSessionById,
  insertChatSession,
  mapChatSessionFromDB,
  findExistingStreamByParticipants,
  updateChatSessionActivity,
} from '../lib/supabase/queries/chat-session-queries';

import {
  buildMentionCandidates,
  extractMentionIdsFromContent,
} from '../utils/mentionExtraction';
import { planMeetNotifications, shouldSkipFeedEvents } from './stream/sendPlan';
import { resolveMessageShare } from './stream/shareAttach';
import { invokeAgentsForMessage } from './stream/agentInvoke';
import { attachStreamRealtime, cleanFreshMessages } from './stream/realtime.ts';
import {
  type MeetingOptions,
  type MeetingOptionsRow,
  meetingOptionsFromRow,
  meetingOptionsToRow,
  plannedMeetingWindow,
} from '../features/time/meetingOptions';
import { createGoogleCalendarEventForMeeting, upsertMeetingCalendarEvent } from '../services/calendarService';
import { belongsToActive, sortAsc } from './stream/realtimeReducer.ts';
import {
  CACHE_TTL_MS,
  buildStreamKey,
  isRealtimeChannelUsable,
  upsertStreamCacheEntry,
  type StreamCacheEntry,
} from './stream/streamCache.ts';

/* ----------------------------------------------------------------------------------------------
 * Debug
 * --------------------------------------------------------------------------------------------*/
const DEBUG = true;
const dlog  = (...a: any[]) => DEBUG && console.log('[StreamStore]', ...a);
const dwarn = (...a: any[]) => DEBUG && console.warn('[StreamStore]', ...a);
const derr  = (...a: any[]) => console.error('[StreamStore]', ...a);

// Aliases so later calls to log/warn/err don’t explode
const log = dlog;
const warn = dwarn;
const err = derr;

/* ----------------------------------------------------------------------------------------------
 * Types
 * --------------------------------------------------------------------------------------------*/

export type StreamScrollState = {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  wasNearBottom: boolean;
  updatedAt: number;
};

interface AddStreamMessageArgs {
  streamType: Comment['streamType'];          // 'direct_message' | 'team_stream' | 'company_stream' | 'standup_stream'
  recipientStreamId: string;                   // For DM: the other user's id ("them"). For others: the stream id.
  content: string;
  parentCommentId?: string;
  mentions?: string[];
  sharedItemId?: string;
  sharedItemDetails?: Comment['sharedItemDetails'];
  tags?: string[];
  // December 23, 2025: FRICTIONLESS URL SHARING
  // Flag to skip auto-URL extraction (for manual shares via ShareDialog)
  skipAutoUrlExtraction?: boolean;
  // January 20, 2026: Inline share draft from composer (previews + metadata edits)
  inlineShare?: InlineShareDraft;
}

interface StreamState {
  // Data
  messages: Comment[];
  chatSessions: ChatSession[];
  messageCacheByStream: Record<string, StreamCacheEntry>;
  scrollStateByStream: Record<string, StreamScrollState>;

  // Proposed actions parsed from agent messages
  actionsByMessageId: Record<string, ProposedAction[]>;

  // September 23, 2025 2:00 PM: Added state for typing users
  // Maps userId to timestamp of last typing activity
  typingUsers: Record<string, number>;

  // Last User for Focus
  lastPersonDMUserId: string | null;
  lastAgentDMUserId: string | null;  

  // UI / status
  isLoading: boolean;
  error: string | null;
  isLoadingSessions: boolean;
  sessionError: string | null;

  // Realtime
  realtimeChannel: RealtimeChannel | null;
  // September 23, 2025 2:00 PM: Added ref for typing status cleanup interval
  _typingCleanupInterval: number | null;

  // Pagination (backfill)
  hasMoreMessages: boolean;
  messageOffset: number;
  messageLimit: number;

  // Active stream context (important for realtime filtering)
  activeStreamType?: Comment['streamType'];
  activeRecipientStreamId?: string;

  // Sidebar selection memory — REQUIRED by your Sidebar.tsx
  selectedTeamStreamId: string | null;
  selectedStandupId: string | null;
  selectedDMUserId: string | null;
  selectedServiceType: string | null; // December 23, 2025: Service stream selection

  // Required setters — USED by Sidebar.tsx
  setSelectedTeamStreamId: (id: string | null) => void;
  setSelectedStandupId: (id: string | null) => void;
  setSelectedDMUserId: (id: string | null) => void;
  setSelectedServiceType: (type: string | null) => void;
  setStreamScrollState: (streamKey: string, state: StreamScrollState) => void;

  // Core actions
  initializeStreamMessages: (
    streamType: Comment['streamType'],
    recipientStreamId: string
  ) => Promise<void>;

  // Approve a stored proposed action by message id + index
  approveProposedAction: (
    messageId: string,
    actionIndex?: number,
    /** September 2026 (Currents 2.0): originating stream, when approving from outside the room. */
    target?: { streamType?: string; recipientStreamId?: string }
  ) => Promise<void>;
  /** September 2026 (Currents 2.0): decline a proposal locally (hide its contract). */
  dismissProposedActions: (messageId: string) => void;

  addStreamMessage: (data: AddStreamMessageArgs) => Promise<{ id: string } | undefined>;
  updateStreamMessage: (messageId: string, content: string) => Promise<void>;
  updateStreamMessageSharedItemDetails: (messageId: string, sharedItemDetails: any) => Promise<Comment>;
  deleteStreamMessage: (messageId: string) => Promise<void>;
  addReactionToStreamMessage: (messageId: string, emoji: string) => Promise<void>;
  removeReactionFromStreamMessage: (messageId: string, emoji: string) => Promise<void>;

  // Realtime lifecycle
  stopRealtime: () => void;
  cleanupRealtimeSubscription: () => void;
  reconnectActiveChannels: () => Promise<void>; // February 2026: Dormancy recovery
  // June 2026: Soft backfill of the active stream (merge-only, never blanks the list).
  // Used to heal the conversation when the comments realtime channel misses an insert
  // (e.g. an @mention arriving while the tab was idle) but the feed_events channel
  // reliably delivered the notification.
  refreshActiveStream: () => Promise<void>;

  // Sessions
  createStandupSession: (data: MeetingOptions & {
    name: string;
    participants: string[];
    isHelpSession?: boolean; // Flag for help-specific initialization
  }) => Promise<ChatSession | null>;
  updateStandupSession: (sessionId: string, data: MeetingOptions & {
    name?: string;
    participants?: string[];
  }) => Promise<void>;
  fetchRecentStandupSessions: () => Promise<void>;
  fetchAllChatSessions: (streamType?: ChatSession['streamType'], specificSessionId?: string) => Promise<void>;
  /** Keep chatSessions (meetings list) live via company chat_sessions changes. */
  ensureChatSessionsRealtime: () => void;
  endStandupSession: (sessionId: string) => Promise<void>;
  endChatStream: (recipientStreamId: string) => Promise<void>;

  // Pagination
  loadMoreStreamMessages: () => Promise<void>;

  // September 23, 2025 2:00 PM: Added typing status actions
  sendTypingStatus: (isTyping: boolean) => void;
  updateTypingStatus: (userId: string, timestamp: number) => void;
}
// ── Agent typing keep-alive (module-level) ──────────────────────────────────────────────
const agentTypingKeepAlive = new Map<string, number>();

/** Company-wide chat_sessions channel so the Meetings gutter list stays live. */
let chatSessionsRealtimeChannel: RealtimeChannel | null = null;
let chatSessionsRealtimeCompanyId: string | null = null;

function stopAgentTypingKeepAlive(agentId?: string) {
  if (!agentId) return;
  const t = agentTypingKeepAlive.get(agentId);
  if (t != null) {
    window.clearInterval(t);
    agentTypingKeepAlive.delete(agentId);
  }
  // also clear the visual typing status
  try {
    useStreamStore.getState().updateTypingStatus(agentId, 0);
  } catch {}
}

/** Show an agent as typing now, refresh every 3s so it doesn't expire, give up after 60s. */
function startAgentTypingKeepAlive(agentId: string) {
  const touch = () => useStreamStore.getState().updateTypingStatus(agentId, Date.now());
  touch();
  stopAgentTypingKeepAlive(agentId);
  agentTypingKeepAlive.set(agentId, window.setInterval(touch, 3000));
  window.setTimeout(() => stopAgentTypingKeepAlive(agentId), 60000);
}

/* ----------------------------------------------------------------------------------------------
 * Helpers
 * --------------------------------------------------------------------------------------------*/

// IMPORTANT: for DMs, both users must subscribe to the SAME topic; sort ids to make it stable.
function getChannelTopic(
  streamType: Comment['streamType'],
  me: string,
  them: string
) {
  return streamType === 'direct_message'
    ? `dm_${[me, them].sort().join('_')}`
    : `${streamType}_${them}`;
}

/**
 * Ensure a chat session exists for a given stream
 * Creates a session if one doesn't exist (for DMs and team streams)
 * Standups create their own sessions explicitly via createStandupSession
 */
async function ensureChatSessionExists(
  recipientStreamId: string,
  streamType: Comment['streamType'],
  companyId: string,
  userId: string
): Promise<void> {
  // Only auto-create for DMs and team streams (standups are created explicitly)
  if (streamType !== 'direct_message' && streamType !== 'team_stream') {
    return;
  }

  try {
    // Check if session already exists
    const { data: existing } = await supabase
      .from('chat_sessions')
      .select('id')
      .eq('recipient_stream_id', recipientStreamId)
      .eq('stream_type', streamType)
      .eq('company_id', companyId)
      .eq('is_stream_ended', false)
      .limit(1)
      .maybeSingle();

    if (existing) {
      // Session exists, no need to create
      return;
    }

    // Create new session
    const now = new Date().toISOString();
    const sessionData: any = {
      id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2),
      company_id: companyId,
      stream_type: streamType,
      recipient_stream_id: recipientStreamId,
      start_time: now,
      last_activity_at: now,
      created_by: userId,
      is_stream_ended: false,
    };

    // For DMs, set user_ids to include both participants
    if (streamType === 'direct_message') {
      sessionData.user_ids = [userId, recipientStreamId].sort();
    }

    await insertChatSession(sessionData);
    console.log('✅ [ensureChatSessionExists] Created new chat session:', {
      recipientStreamId,
      streamType,
      sessionId: sessionData.id
    });
  } catch (error) {
    console.error('❌ [ensureChatSessionExists] Error:', error);
    // Don't throw - this is a non-critical operation
  }
}

/**
 * Named meeting → one company_calendar_events row (shown or hidden by the toggle) and, when asked,
 * one Google event on the creator's calendar. Idempotent: edit re-runs it. Failures never block the
 * room from opening; they are logged and the stored options keep whatever Google id we already have.
 */
async function syncMeetingCalendar(
  sessionId: string,
  title: string,
  participantUserIds: string[],
  options: MeetingOptions,
): Promise<MeetingOptionsRow> {
  const currentUser = useUserStore.getState().currentUser;
  if (!currentUser?.companyId) return meetingOptionsToRow(options);
  const allUsers = useUserStore.getState().allUsers;

  let googleEventId = options.googleEventId ?? null;
  if (options.addToGoogle && !googleEventId) {
    const window = plannedMeetingWindow(options, Date.now());
    const attendeeEmails = [
      ...participantUserIds.map((id) => allUsers.find((u) => u.id === id)?.email).filter((e): e is string => !!e),
      ...(options.externalParticipants ?? []),
    ];
    const g = await createGoogleCalendarEventForMeeting({
      title,
      startIso: window.startIso,
      endIso: window.endIso,
      attendeeEmails,
      chatSessionId: sessionId,
    });
    if (g.success && g.eventId) {
      googleEventId = g.eventId;
      await supabase
        .from('chat_sessions')
        .update({ meeting_options: meetingOptionsToRow({ ...options, googleEventId }) })
        .eq('id', sessionId);
    } else {
      console.warn('⚠️ Google Calendar event not created (meeting still saved):', g.error);
    }
  }

  const r = await upsertMeetingCalendarEvent({
    chatSessionId: sessionId,
    companyId: currentUser.companyId,
    createdByUserId: currentUser.id,
    title,
    participantUserIds,
    options: { ...options, googleEventId },
  });
  if (!r.success) console.warn('⚠️ Meeting not placed on the team calendar (meeting still saved):', r.error);
  return meetingOptionsToRow({ ...options, googleEventId });
}

/* ----------------------------------------------------------------------------------------------
 * Store
 * --------------------------------------------------------------------------------------------*/
const useStreamStore = create<StreamState>((set, get) => ({
  // Data
  messages: [],
  chatSessions: [],
  messageCacheByStream: {},
  scrollStateByStream: {},
  // Parsed proposed actions keyed by messageId
  actionsByMessageId: {},
  // September 23, 2025 2:00 PM: Initialize typingUsers state
  typingUsers: {},

  // Last User for Focus
  lastPersonDMUserId: null,
  lastAgentDMUserId: null,

  // UI / status
  isLoading: false,
  error: null,
  isLoadingSessions: false,
  sessionError: null,

  // Realtime
  realtimeChannel: null,
  // September 23, 2025 2:00 PM: Initialize typing cleanup interval
  _typingCleanupInterval: null,

  // Pagination
  hasMoreMessages: true,
  messageOffset: 0,
  messageLimit: 50,

  // Active stream
  activeStreamType: undefined,
  activeRecipientStreamId: undefined,

  // Sidebar selection memory + setters (REQUIRED by Sidebar.tsx)
  selectedTeamStreamId: null,
  selectedStandupId: null,
  selectedDMUserId: null,
  selectedServiceType: null,

  setSelectedTeamStreamId: (id) => set({ selectedTeamStreamId: id }),
  setSelectedStandupId:   (id) => set({ selectedStandupId: id }),
  setSelectedDMUserId: (id) => {
    set({ selectedDMUserId: id });
    if (!id) return;
    const u = useUserStore.getState().allUsers.find(x => x.id === id);
    if (!u) return;
    if (u.isAgent) set({ lastAgentDMUserId: id });
    else           set({ lastPersonDMUserId: id });
  },
  setSelectedServiceType: (type) => set({ selectedServiceType: type }),
  setStreamScrollState: (streamKey, state) => set((s) => ({
    scrollStateByStream: {
      ...s.scrollStateByStream,
      [streamKey]: state,
    },
  })),

  /* ------------------------------------------------------------------------------------------
   * initializeStreamMessages
   * - sets active context
   * - subscribes realtime (broad to company, filtered in handler)
   * - backfills from DB (DM backfill is SYMMETRIC)
   * ----------------------------------------------------------------------------------------*/
  initializeStreamMessages: async (streamType, recipientStreamId) => {
    const currentUser = useUserStore.getState().currentUser;
    log('initializeStreamMessages called.', { streamType, recipientStreamId });
    if (!streamType) {
      warn('initializeStreamMessages: no streamType; abort init.');
      return;
    }

    // December 2, 2025: Simplified with mutual exclusivity - only one view loads at a time
    // December 4, 2025: CRITICAL FIX - also check if realtime channel is still active!
    // The useEffect cleanup in StreamPage can remove the channel when dependencies change,
    // but we were skipping re-subscription because we thought we were "already viewing".
    const { messages, activeStreamType, activeRecipientStreamId, realtimeChannel } = get();
    const hasUsableRealtimeChannel = isRealtimeChannelUsable(realtimeChannel);
    const currentStreamKey = buildStreamKey(activeStreamType, activeRecipientStreamId);
    const nextStreamKey = buildStreamKey(streamType, recipientStreamId);

    // Persist previous stream cache before switching
    if (currentStreamKey && currentStreamKey !== nextStreamKey && messages.length > 0) {
      set((state) => ({
        messageCacheByStream: upsertStreamCacheEntry(state, currentStreamKey, state.messages),
      }));
    }

    const cachedEntry = nextStreamKey ? get().messageCacheByStream[nextStreamKey] : null;
    const shouldHydrateFromCache = !!cachedEntry && cachedEntry.messages.length > 0;
    const cacheFresh = !!cachedEntry && Date.now() - cachedEntry.updatedAt < CACHE_TTL_MS;
    if (
      activeStreamType === streamType &&
      activeRecipientStreamId === recipientStreamId &&
      messages.length > 0 &&
      hasUsableRealtimeChannel
    ) {
      log('initializeStreamMessages: Already viewing with active channel, skipping');
      return;
    }
    
    // December 4, 2025: If same stream but channel was removed, ONLY re-subscribe
    // Don't clear messages or refetch - just recreate the realtime channel
    // This prevents scroll position reset when chatSessions update triggers useEffect
    const isResubscribeOnly = activeStreamType === streamType && 
                               activeRecipientStreamId === recipientStreamId && 
                               messages.length > 0 && 
                               !hasUsableRealtimeChannel;
    
    if (isResubscribeOnly) {
      log('initializeStreamMessages: Same stream but channel was removed, re-subscribing only (keeping messages)...');
      // Don't clear messages or set isLoading - just recreate channel below
    } else {
      // Full initialization - new stream
      if (shouldHydrateFromCache && cachedEntry) {
        set({
          isLoading: !cacheFresh,
          error: null,
          messages: cachedEntry.messages,
          messageOffset: cachedEntry.messageOffset,
          hasMoreMessages: cachedEntry.hasMoreMessages,
          activeStreamType: streamType,
          activeRecipientStreamId: recipientStreamId,
          // September 23, 2025 2:00 PM: Clear typing users when stream changes
          typingUsers: {},
        });
      } else {
        set({
          isLoading: true,
          error: null,
          messages: [],
          messageOffset: 0,
          hasMoreMessages: true,
          activeStreamType: streamType,
          activeRecipientStreamId: recipientStreamId,
          // September 23, 2025 2:00 PM: Clear typing users when stream changes
          typingUsers: {},
        });
      }
    }

    try {
      if (!currentUser?.companyId) {
        warn('No companyId; abort init.');
        set({ isLoading: false });
        return;
      }

      const companyId: string = currentUser.companyId as string;
      const me = currentUser.id;
      const them = recipientStreamId;
      const topic = getChannelTopic(streamType, me, them);

      // 1) Clean previous channel and cleanup interval
      get().stopRealtime();
      // September 23, 2025 2:00 PM: Clear existing typing cleanup interval
      const cleanupId = get()._typingCleanupInterval;
      if (cleanupId != null) {
        window.clearInterval(cleanupId);
        set({ _typingCleanupInterval: null });
      }

      // 2) Create channel — handlers, reducer, health and backfill live in stream/realtime.ts
      const channel = attachStreamRealtime({
        host: {
          get,
          set,
          onSenderPosted: stopAgentTypingKeepAlive,
          touchSession: (rid, st) => {
            ensureChatSessionExists(rid, st, companyId, me)
              .catch((e) => derr('realtime INSERT: ensure chat session failed:', e));
            updateChatSessionActivity(rid, st, companyId)
              .catch((e) => derr('realtime INSERT: update chat session activity failed:', e));
          },
        },
        topic,
        companyId,
        me,
        streamType,
        recipientStreamId,
        isResubscribeOnly,
        log: dlog,
        warn: dwarn,
        error: derr,
      });

      set({ realtimeChannel: channel });

      // September 23, 2025 2:00 PM: Start typing status cleanup interval
      const cleanupInterval = window.setInterval(() => {
        const TYPING_TIMEOUT_MS = 5000; // 5 seconds
        set((state) => {
          const now = Date.now();
          const newTypingUsers: Record<string, number> = {};
          for (const userId in state.typingUsers) {
            if (state.typingUsers[userId] > now - TYPING_TIMEOUT_MS) {
              newTypingUsers[userId] = state.typingUsers[userId];
            }
          }
          return { typingUsers: newTypingUsers };
        });
      }, 2000); // Check every 2 seconds
      set({ _typingCleanupInterval: cleanupInterval });

    } catch (e0: any) {
      err('Error initializing stream:', e0);
      set({ error: `Failed to initialize stream: ${e0.message}`, isLoading: false });
    }
  },

  /* ------------------------------------------------------------------------------------------
   * addStreamMessage (optimistic → DB → broadcasts → agent orchestration)
   * ----------------------------------------------------------------------------------------*/
  addStreamMessage: async (data) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.id || !currentUser.companyId) {
      err('addStreamMessage: missing current user/company');
      return;
    }

    console.log('streamStore addStreamMessage called with:', { 
      content: data.content, 
      mentions: data.mentions, 
      tags: data.tags,
      streamType: data.streamType,
      recipientStreamId: data.recipientStreamId
    });

    const recipientId = (data.recipientStreamId || '').trim();
    if (!recipientId) {
      err('addStreamMessage: missing recipientStreamId');
      return;
    }

    // 👇 ADD THIS CHECK
    if (!data.streamType) {
      err('addStreamMessage: missing streamType');
      return;
    }

    // AGENT-ONLY: detect mentions
    const allUsers = useUserStore.getState().allUsers;
    const teamStore = useTeamStore.getState();
    const companyStore = useCompanyStore.getState();
    const chatSessionsForMentions = get().chatSessions;
    const meetingSessions = (chatSessionsForMentions || []).filter(
      (s) => s.streamType === 'standup_stream' && s.name,
    );

    const mentionCandidates = buildMentionCandidates({
      allUsers,
      teams: teamStore.teams || [],
      meetingSessions: meetingSessions.map((session) => ({
        id: session.id,
        name: session.name!,
      })),
      company: companyStore.company,
    });

    // Stage: resolve mentions = explicit chips ∪ plain "@text" matches. Agents are never
    // implicitly mentioned by text (that caused uninvited replies); they must be addressed.
    const detectedMentions = new Set<string>(data.mentions || []);
    for (const id of extractMentionIdsFromContent(data.content, mentionCandidates)) {
      detectedMentions.add(id);
    }
    data.mentions = Array.from(detectedMentions);

    // Optimistic message
    const optimistic: Comment = {
      id: `temp-${crypto.randomUUID?.() ?? Date.now()}`,
      userId: currentUser.id,
      content: data.content,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      reactions: {},
      streamType: data.streamType,
      recipientStreamId: recipientId,
      parentCommentId: data.parentCommentId,
      mentions: data.mentions || [],
      tags: data.tags || [],
      entityType: 'stream_message',
      entityId: recipientId,
      sharedItemId: data.sharedItemId,
      sharedItemDetails: data.sharedItemDetails,
    };

    set((s) => {
      // Only the room being viewed shows the optimistic copy. Sending into another room (the
      // composer's `@jay hi` from the feed, a hidden panel) must not paint the message into —
      // and cache it under — whatever happens to be on screen. Realtime guards the same way.
      if (!belongsToActive(optimistic, s.activeStreamType, s.activeRecipientStreamId, currentUser.id)) return s;
      const nextMessages = sortAsc([...s.messages, optimistic]);
      const cacheKey = buildStreamKey(s.activeStreamType, s.activeRecipientStreamId);
      return {
        messages: nextMessages,
        messageCacheByStream: cacheKey
          ? upsertStreamCacheEntry(s, cacheKey, nextMessages)
          : s.messageCacheByStream,
      };
    });

    try {
      // Stage: share. Inline draft or URL-only message → shared item + preview (stream/shareAttach.ts).
      const share = await resolveMessageShare({
        content: data.content,
        streamType: data.streamType,
        recipientId,
        sender: currentUser,
        inlineShare: data.inlineShare,
        sharedItemId: data.sharedItemId,
        skipAutoUrlExtraction: data.skipAutoUrlExtraction,
      });
      const autoCreatedSharedItemId = share.sharedItemId;
      const autoCreatedSharedItemDetails = share.sharedItemDetails;

      // Insert to DB (use auto-created shared item if available, otherwise use provided one)
      const dbInsert = {
        user_id: currentUser.id,
        company_id: currentUser.companyId,
        content: data.content,
        stream_type: data.streamType,
        recipient_stream_id: recipientId,
        parent_comment_id: data.parentCommentId || null,
        mentions: data.mentions || [],
        tags: data.tags || [],
        reactions: {},
        entity_type: 'stream_message',
        entity_id: recipientId,
        shared_item_id: autoCreatedSharedItemId || data.sharedItemId || null,
        shared_item_details: autoCreatedSharedItemDetails ?? (data.sharedItemDetails ?? null),
      };
      const newMessage = await insertStreamMessage(dbInsert);

      // Update optimistic message with real ID and shared item details
      set((s) => {
        const nextMessages = s.messages.map((m) =>
          m.id === optimistic.id ? { 
            ...m, 
            id: newMessage.id,
            sharedItemId: autoCreatedSharedItemId || data.sharedItemId || m.sharedItemId,
            sharedItemDetails: autoCreatedSharedItemDetails || data.sharedItemDetails || m.sharedItemDetails,
          } : m
        );
        const cacheKey = buildStreamKey(s.activeStreamType, s.activeRecipientStreamId);
        return {
          messages: nextMessages,
          messageCacheByStream: cacheKey
            ? upsertStreamCacheEntry(s, cacheKey, nextMessages)
            : s.messageCacheByStream,
        };
      });

      // Ensure chat session exists and update activity timestamp
      await ensureChatSessionExists(recipientId, data.streamType, currentUser.companyId, currentUser.id);
      
      console.log('📤 [addStreamMessage] Calling updateChatSessionActivity for:', {
        recipientId,
        streamType: data.streamType,
        companyId: currentUser.companyId
      });
      await updateChatSessionActivity(recipientId, data.streamType, currentUser.companyId).catch(err =>
        console.error('❌ [addStreamMessage] Failed to update chat session activity:', err)
      );
      
      // Refresh chat sessions to update the conversation list
      await get().fetchAllChatSessions().catch(err =>
        console.error('❌ [addStreamMessage] Failed to refresh chat sessions:', err)
      );

      // Broadcast for snappy peer updates
      const ch = get().realtimeChannel;
      ch?.send({ type: 'broadcast', event: 'stream_message', payload: { comment: newMessage } });

      // Feed events (unchanged)
      const feed = useFeedStore.getState();
      
      // Stage: Meet started → one feed event per room member (pure plan in stream/sendPlan.ts).
      // Messages to agents never create feed events. Plain messages never do either: unread dots
      // come from the comments table, not feed_events. Megaphone (#announcement) is deferred
      // (announcementUtils.containsAnnouncementHashtag is false) and no longer has a branch here.
      const skipFeed = shouldSkipFeedEvents({
        streamType: data.streamType, recipientId, tags: data.tags, content: data.content, allUsers,
      });
      if (!skipFeed) {
        const meetPlan = planMeetNotifications({
          streamType: data.streamType,
          recipientId,
          sender: currentUser,
          sharedItemDetails: data.sharedItemDetails,
          allUsers,
          teams: useTeamStore.getState().teams || [],
          sessions: get().chatSessions,
        });
        for (const n of meetPlan) {
          await feed.addEvent({
            type: 'meet_started',
            title: n.title,
            description: n.description,
            userId: n.userId,
            initiatorId: currentUser.id,
            commentId: newMessage.id,
            streamType: data.streamType,
            recipientStreamId: recipientId,
            sharedItemDetails: data.sharedItemDetails,
          });
        }
      }

      // Stage: mention fan-out — server expands recipients (private-team / DM / standup gated).
      if ((data.mentions || []).length > 0) {
        const { error: fanErr } = await supabase.rpc('fan_out_comment_mentions', { p_comment_id: newMessage.id });
        if (fanErr) err('fan_out_comment_mentions:', fanErr.message);
      }

      // Agents: decided in stream/agentTrigger.ts (pure), invoked in stream/agentInvoke.ts.
      // Never throws; a failed agent wake must not undo a sent message.
      await invokeAgentsForMessage({
        message: { streamType: data.streamType, content: data.content, mentions: data.mentions, tags: data.tags },
        recipientId,
        currentUser,
        allUsers,
        getState: () => get(),
        belongsToActive,
        typing: { start: startAgentTypingKeepAlive },
      });

      // Return message ID for calendar event creation
      return { id: newMessage.id };
    } catch (e2) {
      err('Error adding stream message, reverting optimistic:', e2);
      set((s) => {
        const nextMessages = s.messages.filter((m) => m.id !== optimistic.id);
        const cacheKey = buildStreamKey(s.activeStreamType, s.activeRecipientStreamId);
        return {
          messages: nextMessages,
          messageCacheByStream: cacheKey
            ? upsertStreamCacheEntry(s, cacheKey, nextMessages)
            : s.messageCacheByStream,
        };
      });
      return undefined;
    }
  },

  /* ------------------------------------------------------------------------------------------
   * Approve a proposed action (CONFIRM_ACTION → edge function)
   * ----------------------------------------------------------------------------------------*/
  approveProposedAction: async (messageId, actionIndex = 0, target) => {
    const state = get();
    const actions = state.actionsByMessageId[messageId] || [];
    const action = actions[actionIndex];
    if (!action) {
      console.warn('approveProposedAction: no action for messageId', messageId);
      return;
    }

    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) {
      console.error('approveProposedAction: missing current user/companyId');
      return;
    }

    // September 2026 (Currents 2.0): decisions can be approved from outside the
    // room they were proposed in (entity block in the stream). Callers may pass
    // the originating stream explicitly; otherwise fall back to the active one.
    const streamType = (target?.streamType as Comment['streamType'] | undefined) ?? state.activeStreamType!;
    const recipientStreamId = target?.recipientStreamId ?? state.activeRecipientStreamId!;
    const allUsers = useUserStore.getState().allUsers;

    // Resolve agent id: DM target if agent, else fallback to any agent
    let agentId: string | undefined;
    if (streamType === 'direct_message') {
      const recip = allUsers.find(u => u.id === recipientStreamId);
      if (recip?.isAgent) agentId = recip.id;
    }
    if (!agentId) {
      const anyAgent = allUsers.find(u => u.isAgent);
      agentId = anyAgent?.id;
    }
    if (!agentId) {
      console.error('approveProposedAction: could not resolve agentId');
      return;
    }

    const confirmPrompt = `CONFIRM_ACTION:${JSON.stringify(action)}`;
    const agentUser = allUsers.find((u) => u.id === agentId);
    const viaTeambots =
      agentUser?.externalAgentSource === 'teambots' && !!agentUser.teambotsAgentId;

    if (viaTeambots) {
      const { error } = await supabase.functions.invoke('teambots-integration', {
        body: {
          action: 'currents_chat',
          companyId: currentUser.companyId,
          teambotsAgentId: agentUser.teambotsAgentId,
          localAgentId: agentId,
          senderId: currentUser.id,
          streamType,
          recipientStreamId,
          messages: [{ role: 'user', content: confirmPrompt }],
          activityType: 'channel_message',
          appContext: {
            streamType,
            streamId: recipientStreamId,
            senderId: currentUser.id,
            senderDisplayName: currentUser.casualName || currentUser.name,
            senderName: currentUser.name,
            senderEmail: currentUser.email || undefined,
          },
        },
      });
      if (error) {
        console.error('approveProposedAction teambots error:', error);
        throw error;
      }
    } else {
      const payload = {
        sender_id: currentUser.id,
        agent_id: agentId,
        company_id: currentUser.companyId,
        conversation_context: [],
        original_prompt: confirmPrompt,
        original_stream_type: streamType,
        original_recipient_stream_id: recipientStreamId,
      };
      const { error } = await supabase.functions.invoke('agent-orchestrator', { body: payload });
      if (error) {
        console.error('approveProposedAction invoke error:', error);
        throw error;
      }
    }

    // Once approved, hide the card for this message (realtime will bring in agent's confirmation)
    set(s => {
      const next = { ...s.actionsByMessageId };
      delete next[messageId];
      return { actionsByMessageId: next };
    });
  },

  dismissProposedActions: (messageId) => {
    set(s => {
      if (!s.actionsByMessageId[messageId]) return {};
      const next = { ...s.actionsByMessageId };
      delete next[messageId];
      return { actionsByMessageId: next };
    });
  },

  /* ------------------------------------------------------------------------------------------ */
  stopRealtime() {
    const ch = get().realtimeChannel;
    if (ch) {
      log('Unsubscribing previous stream channel:', (ch as any).topic);
      supabase.removeChannel(ch);
      set({ realtimeChannel: null });
    }
  
    // Clear the periodic pruning of typingUsers
    const interval = get()._typingCleanupInterval;
    if (interval != null) {
      window.clearInterval(interval);
      set({ _typingCleanupInterval: null });
    }

    // 🔹 NEW: clear any agent typing keep-alives
    agentTypingKeepAlive.forEach((t) => window.clearInterval(t));
    agentTypingKeepAlive.clear();
  },

  cleanupRealtimeSubscription: () => {
    const ch = get().realtimeChannel;
    if (ch) {
      log('Cleanup: remove channel', (ch as any).topic);
      supabase.removeChannel(ch);
      set({ realtimeChannel: null });
    }
  
    // Clear the periodic pruning of typingUsers
    const interval = get()._typingCleanupInterval;
    if (interval != null) {
      window.clearInterval(interval);
      set({ _typingCleanupInterval: null });
    }

    // 🔹 NEW: clear any agent typing keep-alives
    agentTypingKeepAlive.forEach((t) => window.clearInterval(t));
    agentTypingKeepAlive.clear();
  },

  // February 2026: Dormancy recovery — re-subscribe the active stream channel + backfill
  // June 2026 fix: `initializeStreamMessages` takes the `isResubscribeOnly` short-circuit
  // here (same active stream + messages still in store + channel just torn down), which
  // re-subscribes but SKIPS the DB fetch. That meant messages delivered while the tab was
  // dormant were never pulled in — they only appeared after a full page refresh. So we
  // explicitly force a DB re-fetch + merge via refreshActiveStream() after re-subscribing.
  reconnectActiveChannels: async () => {
    const { activeStreamType, activeRecipientStreamId } = get();
    if (!activeStreamType || !activeRecipientStreamId) {
      console.log('🔄 [StreamStore] No active stream to reconnect.');
      return;
    }
    console.log(`🔄 [StreamStore] Reconnecting active channel: ${activeStreamType} / ${activeRecipientStreamId}`);
    get().cleanupRealtimeSubscription();
    await get().initializeStreamMessages(activeStreamType, activeRecipientStreamId);
    // Always re-fetch from the DB and merge — the re-subscribe path above does not.
    await get().refreshActiveStream();
    console.log('✅ [StreamStore] Active channel reconnected and messages backfilled.');
  },

  // June 2026: Soft backfill — re-fetch the latest messages for the active stream and
  // merge them in WITHOUT tearing down the channel or clearing the visible list.
  // Safe to call frequently; it is a no-op if the active stream changed mid-flight.
  refreshActiveStream: async () => {
    const { activeStreamType, activeRecipientStreamId } = get();
    const currentUser = useUserStore.getState().currentUser;
    if (!activeStreamType || !activeRecipientStreamId || !currentUser?.companyId) return;

    const companyId = currentUser.companyId as string;
    const me = currentUser.id;
    const streamType = activeStreamType;
    const recipientStreamId = activeRecipientStreamId;

    try {
      const LIMIT = 100;
      const freshNewestFirst = await fetchLatestStreamMessages(
        companyId,
        streamType,
        recipientStreamId,
        me,
        LIMIT
      );
      const fresh = [...freshNewestFirst].reverse();
      const actionsMap = cleanFreshMessages(fresh);

      set((state) => {
        // Bail if the user navigated away while we were fetching.
        if (
          state.activeStreamType !== streamType ||
          state.activeRecipientStreamId !== recipientStreamId
        ) {
          return state;
        }

        const byId = new Map<string, Comment>();
        for (const m of state.messages) byId.set(m.id, m); // keep optimistics not yet in DB
        for (const m of fresh) byId.set(m.id, m);
        const merged = sortAsc(Array.from(byId.values()));
        const cacheKey = buildStreamKey(streamType, recipientStreamId);

        return {
          messages: merged,
          messageCacheByStream: cacheKey
            ? upsertStreamCacheEntry(state, cacheKey, merged, {
                hasMoreMessages: freshNewestFirst.length === LIMIT,
                messageOffset: 0,
              })
            : state.messageCacheByStream,
          hasMoreMessages: freshNewestFirst.length === LIMIT,
          actionsByMessageId: {
            ...state.actionsByMessageId,
            ...actionsMap,
          },
        };
      });
    } catch (e) {
      derr('refreshActiveStream failed:', e);
    }
  },

  /* ------------------------------------------------------------------------------------------ */
  updateStreamMessage: async (messageId, content) => {
    try {
      const updated = await updateStreamMessageById(messageId, { content });
      // Update store after DB update
      set((s) => {
        const nextMessages = s.messages.map((m) => (m.id === messageId ? updated : m));
        const cacheKey = buildStreamKey(s.activeStreamType, s.activeRecipientStreamId);
        return {
          messages: nextMessages,
          messageCacheByStream: cacheKey
            ? upsertStreamCacheEntry(s, cacheKey, nextMessages)
            : s.messageCacheByStream,
        };
      });
    } catch (e3: any) {
      err('Error updating stream message:', e3);
      throw e3;
    }
  },

  updateStreamMessageSharedItemDetails: async (messageId, sharedItemDetails) => {
    // Optimistic: compact/large must flip instantly in the stream card.
    set((s) => {
      const newMessages = s.messages.map((m) =>
        m.id === messageId ? { ...m, sharedItemDetails } : m,
      );
      const cacheKey = buildStreamKey(s.activeStreamType, s.activeRecipientStreamId);
      return {
        messages: newMessages,
        messageCacheByStream: cacheKey
          ? upsertStreamCacheEntry(s, cacheKey, newMessages)
          : s.messageCacheByStream,
      };
    });
    try {
      // jsonb column — pass the object (do not JSON.stringify; that double-encodes).
      const updated = await updateStreamMessageById(messageId, {
        shared_item_details: sharedItemDetails ?? null,
      });
      set((s) => {
        const newMessages = s.messages.map((m) => (m.id === messageId ? updated : m));
        const cacheKey = buildStreamKey(s.activeStreamType, s.activeRecipientStreamId);
        return {
          messages: newMessages,
          messageCacheByStream: cacheKey
            ? upsertStreamCacheEntry(s, cacheKey, newMessages)
            : s.messageCacheByStream,
        };
      });
      return updated;
    } catch (e3: any) {
      err('Error updating stream message shared item details:', e3);
      // Keep optimistic UI; persistence can retry later.
      const optimistic = get().messages.find((m) => m.id === messageId);
      if (optimistic) return optimistic;
      throw e3;
    }
  },

  deleteStreamMessage: async (messageId) => {
    try {
      await deleteStreamMessageById(messageId);
      set((s) => {
        const nextMessages = s.messages.filter((m) => m.id !== messageId);
        const cacheKey = buildStreamKey(s.activeStreamType, s.activeRecipientStreamId);
        return {
          messages: nextMessages,
          messageCacheByStream: cacheKey
            ? upsertStreamCacheEntry(s, cacheKey, nextMessages)
            : s.messageCacheByStream,
        };
      });
    } catch (e4: any) {
      err('Error deleting stream message:', e4);
      throw e4;
    }
  },

  addReactionToStreamMessage: async (messageId, emoji) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) return;

    const original = get().messages.find((m) => m.id === messageId);
    if (!original) return;

    try {
      const reactions = { ...(original.reactions ?? {}) } as Record<string, string[]>;
      if (!reactions[emoji]) reactions[emoji] = [];
      if (!reactions[emoji].includes(currentUser.id)) reactions[emoji].push(currentUser.id);

      set((s) => {
        const nextMessages = s.messages.map((m) => (m.id === messageId ? { ...m, reactions } : m));
        const cacheKey = buildStreamKey(s.activeStreamType, s.activeRecipientStreamId);
        return {
          messages: nextMessages,
          messageCacheByStream: cacheKey
            ? upsertStreamCacheEntry(s, cacheKey, nextMessages)
            : s.messageCacheByStream,
        };
      });

      await updateStreamMessageById(messageId, { reactions });
      get().realtimeChannel?.send({
        type: 'broadcast',
        event: 'stream_message_reactions',
        payload: { messageId, reactions },
      });
    } catch (e5: any) {
      err('Error adding reaction:', e5);
      set((s) => {
        const nextMessages = s.messages.map((m) => (m.id === messageId ? original : m));
        const cacheKey = buildStreamKey(s.activeStreamType, s.activeRecipientStreamId);
        return {
          messages: nextMessages,
          messageCacheByStream: cacheKey
            ? upsertStreamCacheEntry(s, cacheKey, nextMessages)
            : s.messageCacheByStream,
        };
      });
      throw e5;
    }
  },

  removeReactionFromStreamMessage: async (messageId, emoji) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) return;

    const original = get().messages.find((m) => m.id === messageId);
    if (!original?.reactions?.[emoji]) return;

    try {
      const reactions = { ...(original.reactions ?? {}) } as Record<string, string[]>;
      reactions[emoji] = reactions[emoji].filter((id) => id !== currentUser.id);
      if (reactions[emoji].length === 0) delete reactions[emoji];

      set((s) => {
        const nextMessages = s.messages.map((m) => (m.id === messageId ? { ...m, reactions } : m));
        const cacheKey = buildStreamKey(s.activeStreamType, s.activeRecipientStreamId);
        return {
          messages: nextMessages,
          messageCacheByStream: cacheKey
            ? upsertStreamCacheEntry(s, cacheKey, nextMessages)
            : s.messageCacheByStream,
        };
      });

      await updateStreamMessageById(messageId, { reactions });
      get().realtimeChannel?.send({
        type: 'broadcast',
        event: 'stream_message_reactions',
        payload: { messageId, reactions },
      });
    } catch (e6: any) {
      err('Error removing reaction:', e6);
      set((s) => {
        const nextMessages = s.messages.map((m) => (m.id === messageId ? original : m));
        const cacheKey = buildStreamKey(s.activeStreamType, s.activeRecipientStreamId);
        return {
          messages: nextMessages,
          messageCacheByStream: cacheKey
            ? upsertStreamCacheEntry(s, cacheKey, nextMessages)
            : s.messageCacheByStream,
        };
      });
      throw e6;
    }
  },

  /* ------------------------------------------------------------------------------------------
   * Standup/session utilities (unchanged API for Sidebar)
   * ----------------------------------------------------------------------------------------*/
  createStandupSession: async (data) => {
    set({ isLoadingSessions: true, sessionError: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) throw new Error('User not in a company.');

      const allUsers = useUserStore.getState().allUsers;

      const userParticipants = data.participants.filter(
        (id) => allUsers.some((u) => u.id === id && !u.isAgent)
      );
      const agentParticipants = data.participants.filter(
        (id) => allUsers.some((u) => u.id === id && u.isAgent)
      );

      // For help sessions, check if there's an existing stream with the same participants
      // If found, reuse its recipient_stream_id so all sessions appear in the same stream
      let recipientStreamId: string;
      if (data.isHelpSession) {
        // First check in-memory store
        const existingStreamInStore = get().chatSessions.find(session =>
          session.streamType === 'standup_stream' &&
          session.userIds?.length === userParticipants.length &&
          session.userIds?.every(id => userParticipants.includes(id)) &&
          session.agentIds?.length === agentParticipants.length &&
          session.agentIds?.every(id => agentParticipants.includes(id))
        );
        
        if (existingStreamInStore) {
          // Reuse the existing recipient_stream_id
          recipientStreamId = existingStreamInStore.recipientStreamId;
          console.log('🎯 Reusing existing help stream from store:', recipientStreamId);
        } else {
          // Check database for ANY existing stream (even ended ones)
          const existingStreamFromDB = await findExistingStreamByParticipants(
            currentUser.companyId,
            'standup_stream',
            userParticipants,
            agentParticipants
          );
          
          if (existingStreamFromDB) {
            recipientStreamId = existingStreamFromDB.recipientStreamId;
            console.log('🎯 Reusing existing help stream from DB:', recipientStreamId);
          } else {
            // No existing stream found, create a new one
            recipientStreamId = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
            console.log('🎯 Creating new help stream:', recipientStreamId);
          }
        }
      } else {
        // Regular standup - always create new stream
        recipientStreamId = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
      }

      const newSessionId = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
      const meetingName = data.isHelpSession
        ? `🎯 Help with ${allUsers.find(u => u.id === agentParticipants[0])?.casualName || allUsers.find(u => u.id === agentParticipants[0])?.name || 'Agent'}`
        : data.name;
      const { isHelpSession: _help, name: _name, participants: _parts, ...meetingOptions } = data;
      void _help; void _name; void _parts;

      const sessionData = {
        id: newSessionId,
        company_id: currentUser.companyId,
        stream_type: 'standup_stream' as Comment['streamType'],
        recipient_stream_id: recipientStreamId, // Use the reused or new recipient_stream_id
        start_time: new Date().toISOString(),
        end_time: null,
        last_activity_at: new Date().toISOString(),
        name: meetingName,
        summary: null, // Summary will be set by AI summarization later
        created_by: currentUser.id,
        user_ids: userParticipants,
        agent_ids: agentParticipants,
        project_id: data.projectId ?? null,
        meeting_options: data.isHelpSession ? {} : meetingOptionsToRow(meetingOptions),
      };

      const newChatSession = await insertChatSession(sessionData);

      // Named meeting on the team calendar (and, when asked, the creator's Google Calendar).
      if (!data.isHelpSession && (data.showOnTeamCalendar || data.addToGoogle)) {
        newChatSession.meeting_options = await syncMeetingCalendar(
          newChatSession.id,
          meetingName,
          userParticipants,
          meetingOptions,
        );
      }

      // Custom initialization for help sessions
      if (data.isHelpSession) {
        // Don't send initial user message for help sessions
        // Bob will receive the session creation and respond with custom greeting
        // The agent-orchestrator will detect this is a help session from the name
      } else {
        // Regular standup initialization
        await get().addStreamMessage({
          streamType: 'standup_stream',
          recipientStreamId: recipientStreamId,
          content: `Standup "${data.name}" has started!`,
          mentions: data.participants,
          tags: [],
        });
      }

      set((s) => ({
        chatSessions: [mapChatSessionFromDB(newChatSession), ...s.chatSessions],
        isLoadingSessions: false,
      }));

      return mapChatSessionFromDB(newChatSession);
    } catch (e7: any) {
      err('Error creating standup session:', e7);
      set({ sessionError: `Failed to create standup: ${e7.message}`, isLoadingSessions: false });
      return null;
    }
  },

  updateStandupSession: async (sessionId: string, data) => {
    set({ isLoadingSessions: true, sessionError: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) throw new Error('User not in a company.');

      const allUsers = useUserStore.getState().allUsers;

      // Separate user and agent participants
      const userParticipants = (data.participants || []).filter(
        (id) => allUsers.some((u) => u.id === id && !u.isAgent)
      );
      const agentParticipants = (data.participants || []).filter(
        (id) => allUsers.some((u) => u.id === id && u.isAgent)
      );

      // Build update object
      const updateData: any = {};
      if (data.name !== undefined) {
        updateData.name = data.name;
      }
      if (data.participants !== undefined) {
        updateData.user_ids = userParticipants;
        updateData.agent_ids = agentParticipants;
      }

      // Meeting options: merge over what is stored so a partial edit does not drop the rest.
      const existing = get().chatSessions.find((s) => s.id === sessionId);
      const prior = meetingOptionsFromRow(existing?.meetingOptions as MeetingOptionsRow | null);
      const { name: _n, participants: _p, ...incoming } = data;
      void _n; void _p;
      const merged: MeetingOptions = { ...prior, ...incoming };
      if (data.projectId !== undefined) updateData.project_id = data.projectId;
      updateData.meeting_options = meetingOptionsToRow(merged);

      const { data: updatedSession, error } = await supabase
        .from('chat_sessions')
        .update(updateData)
        .eq('id', sessionId)
        .eq('company_id', currentUser.companyId)
        .select()
        .single();

      if (error) throw error;

      const calendarTouched =
        merged.showOnTeamCalendar || merged.addToGoogle || prior.showOnTeamCalendar || !!prior.googleEventId;
      if (calendarTouched) {
        updatedSession.meeting_options = await syncMeetingCalendar(
          sessionId,
          updatedSession.name || existing?.name || 'Meeting',
          updatedSession.user_ids || userParticipants,
          merged,
        );
      }

      // December 2, 2025: Notify new participants when they're added to a meeting
      if (data.participants !== undefined) {
        // Get the old session to compare participants
        const oldSession = get().chatSessions.find(s => s.id === sessionId);
        const oldParticipants = new Set([
          ...(oldSession?.userIds || []),
          ...(oldSession?.agentIds || [])
        ]);
        
        // Find newly added participants (excluding current user)
        const newParticipants = data.participants.filter(
          id => !oldParticipants.has(id) && id !== currentUser.id
        );
        
        if (newParticipants.length > 0) {
          const meetingName = data.name || oldSession?.name || oldSession?.summary || 'a meeting';
          const feed = useFeedStore.getState();
          
          for (const participantId of newParticipants) {
            // Only notify human users, not agents
            const isAgent = allUsers.some(u => u.id === participantId && u.isAgent);
            if (!isAgent) {
              console.log(`📬 Notifying ${participantId} about being added to meeting: ${meetingName}`);
              feed.addEvent({
                type: 'standup_participant_added',
                title: 'Added to Meeting',
                description: `${currentUser.name} added you to ${meetingName}`,
                userId: participantId,
                initiatorId: currentUser.id,
                streamType: 'standup_stream',
                recipientStreamId: oldSession?.recipientStreamId || sessionId,
              });
            }
          }
        }
      }
      
      set((s) => ({
        chatSessions: s.chatSessions.map((x) => 
          x.id === sessionId ? mapChatSessionFromDB(updatedSession) : x
        ),
        isLoadingSessions: false,
      }));

      log(`Standup session ${sessionId} updated successfully.`);
    } catch (e8: any) {
      err('Error updating standup session:', e8);
      set({ sessionError: `Failed to update standup: ${e8.message}`, isLoadingSessions: false });
    }
  },

  fetchRecentStandupSessions: async () => {
    set({ isLoadingSessions: true, sessionError: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) return;

      // Pull standups and merge into chatSessions — never wipe DMs/teams.
      const sessions = await fetchChatSessions(currentUser.companyId, 'standup_stream', 40);

      const uniqueSessions = new Map<string, ChatSession>();
      for (const session of sessions) {
        const existing = uniqueSessions.get(session.recipientStreamId);
        if (!existing) {
          uniqueSessions.set(session.recipientStreamId, session);
        } else {
          const existingTime = new Date(existing.lastActivityAt || existing.startTime || 0).getTime();
          const currentTime = new Date(session.lastActivityAt || session.startTime || 0).getTime();
          if (currentTime > existingTime) {
            uniqueSessions.set(session.recipientStreamId, session);
          }
        }
      }

      const standups = Array.from(uniqueSessions.values());
      set((s) => {
        const others = s.chatSessions.filter((c) => c.streamType !== 'standup_stream');
        const byStream = new Map(standups.map((c) => [c.recipientStreamId, c]));
        // Keep any local standup not yet returned (race after create) until the next fetch.
        for (const c of s.chatSessions) {
          if (c.streamType === 'standup_stream' && !byStream.has(c.recipientStreamId)) {
            byStream.set(c.recipientStreamId, c);
          }
        }
        return {
          chatSessions: [...others, ...byStream.values()],
          isLoadingSessions: false,
        };
      });
    } catch (e8: any) {
      err('Error fetching recent standups:', e8);
      set({ sessionError: `Failed to fetch recent standups: ${e8.message}`, isLoadingSessions: false });
    }
  },

  fetchAllChatSessions: async (streamType?: ChatSession['streamType'], specificSessionId?: string) => {
    console.log('🔄 [fetchAllChatSessions] Starting fetch...', { streamType, specificSessionId });
    set({ isLoadingSessions: true, sessionError: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) return;

      let sessions = await fetchChatSessions(currentUser.companyId, streamType);
      
      // If looking for a specific session by ID and not found, fetch it directly
      if (specificSessionId && !sessions.some(s => s.id === specificSessionId)) {
        const specificSession = await fetchChatSessionById(specificSessionId);
        if (specificSession) {
          sessions.push(specificSession);
        }
      }
      
      // Deduplicate by recipient_stream_id - keep only the most recent session per stream
      // This prevents showing multiple chat_sessions for the same standup stream
      // (which can happen when messages are broken into sessions for vector embedding)
      const uniqueSessions = new Map<string, ChatSession>();
      for (const session of sessions) {
        const existing = uniqueSessions.get(session.recipientStreamId);
        if (!existing) {
          uniqueSessions.set(session.recipientStreamId, session);
        } else {
          // Keep the session with the most recent lastActivityAt
          // BUT: if we're looking for a specific session, prioritize it
          const existingTime = new Date(existing.lastActivityAt || existing.startTime || 0).getTime();
          const currentTime = new Date(session.lastActivityAt || session.startTime || 0).getTime();
          if (specificSessionId && session.id === specificSessionId) {
            // Always keep the specific session we're looking for
            uniqueSessions.set(session.recipientStreamId, session);
          } else if (currentTime > existingTime) {
            uniqueSessions.set(session.recipientStreamId, session);
          }
        }
      }
      
      const deduplicatedSessions = Array.from(uniqueSessions.values());
      console.log('✅ [fetchAllChatSessions] Fetched sessions:', {
        totalCount: deduplicatedSessions.length,
        dmSessions: deduplicatedSessions.filter(s => s.streamType === 'direct_message').map(s => ({
          recipientId: s.recipientStreamId,
          lastActivityAt: s.lastActivityAt,
          lastActivityDate: s.lastActivityAt ? new Date(s.lastActivityAt).toLocaleString() : 'null'
        }))
      });
      // Typed fetch merges that slice; unfiltered replace is the full inventory.
      if (streamType) {
        set((s) => {
          const others = s.chatSessions.filter((c) => c.streamType !== streamType);
          return { chatSessions: [...others, ...deduplicatedSessions], isLoadingSessions: false };
        });
      } else {
        set({ chatSessions: deduplicatedSessions, isLoadingSessions: false });
      }
    } catch (e9: any) {
      err('Error fetching sessions:', e9);
      set({ sessionError: `Failed to fetch sessions: ${e9.message}`, isLoadingSessions: false });
    }
  },

  endStandupSession: async (sessionId: string) => {
    set({ isLoadingSessions: true, sessionError: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) throw new Error('No authenticated user or company ID.');

      const { data, error } = await supabase
        .from('chat_sessions')
        .update({ end_time: new Date().toISOString() })
        .eq('id', sessionId)
        .eq('company_id', currentUser.companyId)
        .select()
        .single();

      if (error) throw error;

      set((s) => ({
        chatSessions: s.chatSessions.map((x) => (x.id === sessionId ? mapChatSessionFromDB(data) : x)),
        isLoadingSessions: false,
      }));
      log(`Standup session ${sessionId} ended successfully.`);

      // Generate embedding asynchronously (don't block UI)
      supabase.functions.invoke('summarize-and-embed-session', {
        body: { sessionId }
      }).then(({ data: embedResult, error: embedError }) => {
        if (embedError) {
          console.warn('⚠️ Failed to generate session embedding (non-critical):', embedError);
        } else {
          console.log('✅ Session embedded for semantic memory:', embedResult);
        }
      });
    } catch (e10: any) {
      err('Error ending standup session:', e10);
      set({ sessionError: `Failed to end standup: ${e10.message}`, isLoadingSessions: false });
    }
  },

  endChatStream: async (recipientStreamId: string) => {
    set({ isLoadingSessions: true, sessionError: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) throw new Error('No authenticated user or company ID.');
  
      // Remove `data` so TS doesn't warn about it being unused
      const { error } = await supabase.functions.invoke('end-chat-stream', {
        body: { recipient_stream_id: recipientStreamId },
      });
      if (error) throw error;
  
      set((s) => ({
        chatSessions: s.chatSessions.map((c) =>
          c.recipientStreamId === recipientStreamId ? { ...c, isStreamEnded: true } : c
        ),
        isLoadingSessions: false,
      }));
  
      log(`Chat stream ${recipientStreamId} marked as ended successfully.`);
      get().fetchAllChatSessions();

      // Find the session ID for this stream to generate embedding
      const session = get().chatSessions.find(s => s.recipientStreamId === recipientStreamId);
      if (session?.id) {
        supabase.functions.invoke('summarize-and-embed-session', {
          body: { sessionId: session.id }
        }).then(({ data: embedResult, error: embedError }) => {
          if (embedError) {
            console.warn('⚠️ Failed to generate session embedding (non-critical):', embedError);
          } else {
            console.log('✅ Session embedded for semantic memory:', embedResult);
          }
        });
      }
    } catch (e11: any) {
      err('Error ending chat stream:', e11);
      set({ sessionError: `Failed to end chat stream: ${e11.message}`, isLoadingSessions: false });
    }
  },

  // Time-based paging for older history
  loadMoreStreamMessages: async () => {
    const {
      isLoading,
      hasMoreMessages,
      activeStreamType,
      activeRecipientStreamId,
      messages,
    } = get();

    if (isLoading || !hasMoreMessages || !activeStreamType || !activeRecipientStreamId) return;

    set({ isLoading: true, error: null });

    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) {
        set({ isLoading: false });
        return;
      }

      // earliest message we have; page older than that
      const earliest = messages.length ? messages[0] : null;
      const beforeIso = earliest ? earliest.createdAt : new Date().toISOString();
      const PAGE = 50;

      const older = await fetchMessagesBefore(
        currentUser.companyId,
        activeStreamType,
        activeRecipientStreamId,
        currentUser.id,
        beforeIso,
        PAGE
      );

      // Strip metadata from older messages
      for (const m of older) {
        let content = m.content || '';
        
        // Strip metadata first
        const metadataStart = content.indexOf('[AGENT_CREATED_OBJECTS:[');
        if (metadataStart !== -1) {
          try {
            let bracketCount = 0;
            let jsonStart = metadataStart + '[AGENT_CREATED_OBJECTS:['.length;
            let jsonEnd = jsonStart;
            
            for (let i = jsonStart; i < content.length; i++) {
              if (content[i] === '[') bracketCount++;
              if (content[i] === ']') {
                bracketCount--;
                if (bracketCount === 0) {
                  jsonEnd = i + 1;
                  break;
                }
              }
            }
            
            const beforeMetadata = content.substring(0, metadataStart).trim();
            const afterMetadata = content.substring(jsonEnd + 1).trim();
            content = (beforeMetadata + ' ' + afterMetadata).trim();
            
            // Also remove any partial JSON fragments that might have leaked through
            content = content.replace(/["'][\w_]+["']:\s*(?:null|true|false|"[^"]*"|\d+)[,}\]]*/g, '').trim();
            content = content.replace(/}}]]*/g, '').trim();
            content = content.replace(/\]\]/g, '').trim();
            content = content.replace(/["']:\s*(?:null|true|false|"[^"]*"|\d+)[,}\]]*$/g, '').trim();
          } catch (error) {
            // Fallback to regex if bracket counting fails
            content = content.replace(/\[AGENT_CREATED_OBJECTS:\[[\s\S]*?\]\]/g, '').trim();
            // Also clean partial fragments
            content = content.replace(/["'][\w_]+["']:\s*(?:null|true|false|"[^"]*"|\d+)[,}\]]*/g, '').trim();
            content = content.replace(/}}]]*/g, '').trim();
          }
        } else {
          // Fallback: Try regex-based stripping even if bracket counting didn't find it
          const regexMatch = content.match(/\[AGENT_CREATED_OBJECTS:\[[\s\S]*?\]\]/);
          if (regexMatch) {
            content = content.replace(/\[AGENT_CREATED_OBJECTS:\[[\s\S]*?\]\]/g, '').trim();
            // Also clean partial fragments
            content = content.replace(/["'][\w_]+["']:\s*(?:null|true|false|"[^"]*"|\d+)[,}\]]*/g, '').trim();
            content = content.replace(/}}]]*/g, '').trim();
          }
        }
        
        // Then strip JSON blocks
        const { cleanText } = extractProposedActionsFromText(content);
        m.content = cleanText;
      }
      
      set((state) => {
        const nextMessages = sortAsc([...older, ...state.messages]);
        const cacheKey = buildStreamKey(state.activeStreamType, state.activeRecipientStreamId);
        return {
          messages: nextMessages,
          messageCacheByStream: cacheKey
            ? upsertStreamCacheEntry(state, cacheKey, nextMessages, {
              hasMoreMessages: older.length === PAGE,
            })
            : state.messageCacheByStream,
          isLoading: false,
          hasMoreMessages: older.length === PAGE,
        };
      });
    } catch (e12: any) {
      derr('Error loading more messages:', e12);
      set({ error: `Failed to load more messages: ${e12.message}`, isLoading: false });
    }
  },

  // September 23, 2025 2:00 PM: sendTypingStatus action
  sendTypingStatus: (isTyping: boolean) => {
    const currentUser = useUserStore.getState().currentUser;
    const { realtimeChannel, activeStreamType, activeRecipientStreamId } = get();

    if (currentUser && realtimeChannel && activeStreamType && activeRecipientStreamId) {
      realtimeChannel.send({
        type: 'broadcast',
        event: 'typing_status',
        payload: {
          userId: currentUser.id,
          isTyping: isTyping,
          streamType: activeStreamType,
          recipientStreamId: activeRecipientStreamId,
        },
      });
    }
  },

  // September 23, 2025 2:00 PM: updateTypingStatus action
  updateTypingStatus: (userId: string, timestamp: number) => {
    set((state) => {
      const newTypingUsers = { ...state.typingUsers };
      if (timestamp > 0) {
        newTypingUsers[userId] = timestamp;
      } else {
        delete newTypingUsers[userId];
      }
      return { typingUsers: newTypingUsers };
    });
  },

  ensureChatSessionsRealtime: () => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) return;
    if (chatSessionsRealtimeCompanyId === currentUser.companyId && chatSessionsRealtimeChannel) {
      return;
    }
    if (chatSessionsRealtimeChannel) {
      void supabase.removeChannel(chatSessionsRealtimeChannel);
      chatSessionsRealtimeChannel = null;
      chatSessionsRealtimeCompanyId = null;
    }
    const companyId = currentUser.companyId;
    chatSessionsRealtimeCompanyId = companyId;
    chatSessionsRealtimeChannel = supabase
      .channel(`chat-sessions-${companyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_sessions',
          filter: `company_id=eq.${companyId}`,
        },
        (payload) => {
          const eventType = payload.eventType;
          if (eventType === 'DELETE') {
            const oldRow = payload.old as { id?: string } | null;
            if (!oldRow?.id) return;
            set((s) => ({
              chatSessions: s.chatSessions.filter((c) => c.id !== oldRow.id),
            }));
            return;
          }
          const row = payload.new as Parameters<typeof mapChatSessionFromDB>[0] | null;
          if (!row?.id) return;
          const mapped = mapChatSessionFromDB(row);
          set((s) => {
            const without = s.chatSessions.filter(
              (c) => c.id !== mapped.id && !(
                c.streamType === mapped.streamType
                && c.recipientStreamId === mapped.recipientStreamId
              ),
            );
            return { chatSessions: [...without, mapped] };
          });
        },
      )
      .subscribe();
  },
}));

// Tu Metrics - December 5, 2025
// See: Fortune Cookie #8 "The One Mic Principle"
(useStreamStore as any).tuMetrics = {
  id: 'stream-store',
  tu1: 0.62,  // Very large store (~2700 lines) - candidate for extraction
  tu2: 0.70,  // Complex but functional
  tu3: 9000,  // Core messaging infrastructure
  breakageResistance: 1.7,  // Real-time subscriptions, many consumers
  notes: 'December 2025: Fixed duplicate message bug with final content-based deduplication. ' +
         'Timestamp-based dedup was unreliable (2-second window). ' +
         'One Mic Principle (#8) - mutual exclusivity between StreamPage and FloatingChat. #8',
  lastRefactor: '2025-12-05'
};

export default useStreamStore;